/**
 * A small panel that opens against a button: the grid's column filters and
 * its Columns, Freeze and Download menus.
 *
 * ## Why it is a portal
 *
 * The button sits in a sticky header inside a scroller inside a card, and
 * all three clip - a panel positioned in there came out sliced off at the
 * card's edge, or scrolled away with the rows. At the end of the document
 * there is nothing to clip it. PlanChip does the same for the same reason.
 *
 * ## Why it divides by the zoom
 *
 * The interface draws at `zoom: .7`. A rectangle read back from
 * getBoundingClientRect() is in the screen's pixels, while a `top` written
 * onto an element inside the zoomed page is in the page's own, so a panel
 * told to sit at the button's bottom edge lands at 70% of the way there.
 * The factor is measured off the panel itself - its drawn width over its
 * laid-out width - rather than read from the stylesheet, so it stays right
 * if the zoom changes or a browser ignores it (the ratio is then 1).
 *
 * ## Why it follows rather than closes
 *
 * Ticking a value in a filter shortens the table, which can scroll the page
 * under the panel - and a panel that shut itself every time it was used
 * would be unusable. So it re-reads the button's position whenever the page
 * scrolls, resizes or the panel redraws, and closes only on a click outside
 * it or on Escape (which hands focus back to the button).
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const GAP = 6;
const EDGE = 10;

/** Screen pixels per page pixel for `el`, or 1 when it cannot be told. */
export function zoomOf(el) {
  if (!el) return 1;
  const drawn = el.getBoundingClientRect().width;
  const laid = el.offsetWidth;
  const z = laid ? drawn / laid : 1;
  return Number.isFinite(z) && z > 0 ? z : 1;
}

export default function Popover({
  anchor, open, onClose, label, className = '', align = 'start', role = 'dialog', children
}) {
  const panel = useRef(null);
  const [at, setAt] = useState(null);

  const place = useCallback(() => {
    const a = anchor.current;
    const p = panel.current;
    if (!a || !p) return;
    const z = zoomOf(p);
    const ar = a.getBoundingClientRect();
    const pr = p.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    /* Below the button if it fits, above it if not; never off either side. */
    let left = align === 'end' ? ar.right - pr.width : ar.left;
    left = Math.max(EDGE, Math.min(left, vw - pr.width - EDGE));
    let top = ar.bottom + GAP;
    if (top + pr.height > vh - EDGE && ar.top - GAP - pr.height >= EDGE) top = ar.top - GAP - pr.height;

    const next = { top: top / z, left: left / z };
    setAt(prev => (prev && Math.abs(prev.top - next.top) < 0.5 && Math.abs(prev.left - next.left) < 0.5 ? prev : next));
  }, [anchor, align]);

  /* After every draw: the first to place it, the rest because what is in it
     (a list narrowed by a search) may have changed its height. Settles at
     once - an unchanged position is the same state and draws nothing. */
  useLayoutEffect(() => {
    if (open) place();
    else if (at) setAt(null);
  });

  /* Focus goes into the panel, to whatever asked for it or the first field. */
  const placed = !!at;
  useEffect(() => {
    if (!open || !placed || !panel.current) return;
    const first = panel.current.querySelector('[data-autofocus]')
      || panel.current.querySelector('input, button, select, textarea');
    if (first && !panel.current.contains(document.activeElement)) first.focus({ preventScroll: true });
  }, [open, placed]);

  useEffect(() => {
    if (!open) return undefined;
    let frame = 0;
    const inside = t => (panel.current && panel.current.contains(t)) || (anchor.current && anchor.current.contains(t));
    const down = e => { if (!inside(e.target)) onClose(); };
    const key = e => {
      if (e.key !== 'Escape') return;
      /* Ours, not the sheet's behind it, which also closes on Escape. */
      e.stopPropagation();
      onClose();
      if (anchor.current) anchor.current.focus({ preventScroll: true });
    };
    const follow = e => {
      if (e && e.type === 'scroll' && panel.current && panel.current.contains(e.target)) return;
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(place);
    };
    document.addEventListener('pointerdown', down, true);
    document.addEventListener('keydown', key, true);
    window.addEventListener('scroll', follow, true);
    window.addEventListener('resize', follow);
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener('pointerdown', down, true);
      document.removeEventListener('keydown', key, true);
      window.removeEventListener('scroll', follow, true);
      window.removeEventListener('resize', follow);
    };
  }, [open, onClose, anchor, place]);

  if (!open) return null;

  return createPortal(
    <div
      ref={panel}
      className={`dg-pop ${className}`.trimEnd()}
      role={role}
      aria-label={label}
      /* Drawn at 0,0 to be measured first, and invisible while it is there. */
      style={at ? { top: at.top, left: at.left } : { top: 0, left: 0, visibility: 'hidden' }}
      onClick={e => e.stopPropagation()}
    >
      {children}
    </div>,
    document.body
  );
}
